import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { format } from 'date-fns'
import api from '../services/api'
import { useNavigate, useSearchParams } from 'react-router-dom'
import EmptyState from '../components/EmptyState'
import { SkeletonText, SkeletonCard } from '../components/Skeleton'
import useTableSort from '../hooks/useTableSort'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'
import { useBulkUndo } from '../hooks/useBulkUndo'
import HelpButton from '../components/HelpButton'
import { usePageShortcut } from '../hooks/useKeyboardShortcuts'
import { usePendingDelete } from '../hooks/usePendingDelete'
import SeriesDeleteModal from '../components/SeriesDeleteModal'
import {
  daysUntilDue, isDueWithin, isOverdue, parseDueParam, DUE_SOON_DAYS, todoDueDate, firmDueDay, isDueAfterStartDay,
} from '../utils/todoDue'
import { toFirmWallClock } from '../utils/eventTime'
import { eventTypeLabel, getEventIcon } from '../lib/eventTypes'
import EventHistory from '../components/EventHistory'


const getPriorityInfo = (event) => {
  if (event.is_completed) return {
    label: 'Completed', color: 'bg-gray-100 text-gray-500',
    rowColor: 'bg-gray-50', dot: 'bg-gray-400', days: null, order: 999
  }
  // Shared with the quick-access bar's To-Do badge (utils/todoDue.js) so the
  // labels here, the "Due within N days" filter and the badge all agree.
  // due_date is a DATE column and goes through parseDateOnly there.  `days`
  // is firm CALENDAR days (0 = today); `overdue` also covers a timed to-do
  // whose moment already passed earlier today.  An Amicus to-do stored start
  // day → due day counts to its DUE day (James 2026-10-21, "due date
  // everywhere"), so the chip, the Overdue / Due Today / Due within tabs and
  // their counts all use that day.
  const days = daysUntilDue(event)
  if (days === null) return {
    label: 'No date', color: 'bg-gray-100 text-gray-500',
    rowColor: '', dot: 'bg-gray-400', days: null, order: 998
  }
  if (isOverdue(event)) return {
    label: days < 0
      ? `Overdue by ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''}`
      : 'Overdue (earlier today)',
    color: 'bg-gray-900 text-white', rowColor: 'bg-gray-50 border-l-4 border-gray-800',
    dot: 'bg-gray-900', days, overdue: true, order: -1
  }
  if (days === 0) return {
    label: 'Due today!', color: 'bg-red-600 text-white',
    rowColor: 'bg-red-50 border-l-4 border-red-600', dot: 'bg-red-600', days, order: 0
  }
  if (days === 1) return {
    label: 'Due tomorrow', color: 'bg-red-100 text-red-700',
    rowColor: 'bg-red-50 border-l-4 border-red-400', dot: 'bg-red-500', days, order: 1
  }
  if (days <= 2) return {
    label: `Due in ${days} days`, color: 'bg-red-100 text-red-700',
    rowColor: 'bg-red-50 border-l-4 border-red-300', dot: 'bg-red-400', days, order: days
  }
  if (days <= 6) return {
    label: `Due in ${days} days`, color: 'bg-orange-100 text-orange-700',
    rowColor: 'bg-orange-50 border-l-4 border-orange-400', dot: 'bg-orange-400', days, order: days
  }
  if (days <= 13) return {
    label: `Due in ${days} days`, color: 'bg-yellow-100 text-yellow-700',
    rowColor: 'bg-yellow-50 border-l-4 border-yellow-400', dot: 'bg-yellow-400', days, order: days
  }
  return {
    label: `Due in ${days} days`, color: 'bg-green-100 text-green-700',
    rowColor: 'bg-green-50 border-l-4 border-green-400', dot: 'bg-green-400', days, order: days
  }
}

export default function Tasks() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Feedback #832 — ?due=N (from the quick-access bar's "Open To-Do list →")
  // preselects "Due within N days": incomplete to-dos that are overdue or due
  // within N days.  ?focus=<id> scrolls to and highlights one item.
  const [searchParams] = useSearchParams()
  const dueParam = parseDueParam(searchParams.get('due'))
  const focusParam = parseInt(searchParams.get('focus'), 10) || null
  // ?tab=overdue etc. — lets a link open a specific tab (the quick-access
  // bar's "earlier items are past due" line opens Overdue).  ?due wins.
  const TAB_IDS = ['active', 'overdue', 'today', 'due', 'completed', 'all']
  const tabParam = TAB_IDS.includes(searchParams.get('tab')) ? searchParams.get('tab') : null
  const [dueDays, setDueDays] = useState(() => dueParam || DUE_SOON_DAYS)
  const [filter, setFilter] = useState(() => (dueParam ? 'due' : (tabParam || 'active')))
  const [highlightId, setHighlightId] = useState(null)
  const loadedOnceRef = useRef(false)
  // Tasks page uses cards, not a column-header table — so instead of the
  // click-header pattern used elsewhere, we drive sort via a dropdown +
  // useTableSort for the persistence.  Default is 'priority' which mirrors
  // the original getPriorityInfo ordering.
  const sort = useTableSort('tasks', { key: 'priority', dir: 'asc' })
  const toast = useToast()
  const ask = useAsk()
  const undo = useBulkUndo()
  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  // Feedback #822 (Hestia) — she blocks out date+time to reserve slots for
  // depos and appointments, then needs to remove them.  The Tasks page could
  // complete / reopen / annotate but never delete, so the only way out was to
  // go find the row on the Calendar.  Same endpoint, same undo window, same
  // series-scope picker — just reachable from where she already is.
  const pending = usePendingDelete()
  const [seriesDeletePrompt, setSeriesDeletePrompt] = useState(null)

  const toggleTask = (id) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const clearTaskSelection = () => setSelectedTaskIds(new Set())

  const handleBulkComplete = async () => {
    const ids = Array.from(selectedTaskIds)
    if (ids.length === 0) return
    const notes = await ask({
      title: 'Bulk complete',
      message: `Mark ${ids.length} task${ids.length === 1 ? '' : 's'} as complete?\n\nOptional completion note (applied to all):`,
      type: 'textarea',
      confirmLabel: 'Complete',
    })
    if (notes === null) return // Cancel
    // Snapshot from LOCAL state BEFORE the mutation so undo can restore prior
    // completion state.  Only rows that were NOT already completed will be
    // reopened on revert — already-completed ones stay put.
    const snapshot = events
      .filter((e) => selectedTaskIds.has(e.id))
      .map((e) => ({
        id: e.id,
        is_completed: !!e.is_completed,
      }))
    setBulkBusy(true)
    try {
      const res = await api.put('/api/calendar/bulk/complete', { ids, notes: notes || null })
      clearTaskSelection()
      undo.run({
        ids,
        snapshot: () => snapshot,
        apply: async () => {}, // already fired
        revert: async (snap) => {
          const toReopen = snap.filter((s) => !s.is_completed)
          await Promise.all(toReopen.map((s) =>
            api.put(`/api/calendar/${s.id}/reopen`).catch((err) => {
              // Skip rows the caller no longer has permission for (403) — a
              // paralegal completing on behalf of an attorney shouldn't crash
              // the whole undo.
              const st = err?.response?.status
              if (st === 403 || st === 404) return null
              throw err
            })
          ))
        },
        label: `Completed ${res.data.completed} of ${res.data.requested} task${res.data.requested === 1 ? '' : 's'}.`,
        onAfter: () => fetchEvents(),
      })
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk complete failed')
    } finally { setBulkBusy(false) }
  }

  const handleBulkReschedule = async () => {
    const ids = Array.from(selectedTaskIds)
    if (ids.length === 0) return
    const raw = await ask({
      title: 'Bulk reschedule',
      message: `Shift ${ids.length} task${ids.length === 1 ? '' : 's'} by how many days?\n\n` +
      `Positive number = future (e.g. 7 = a week later)\n` +
      `Negative number = past (e.g. -3 = three days earlier)\n` +
      `Time-of-day is preserved.`,
      defaultValue: '7',
      required: true,
      confirmLabel: 'Reschedule',
    })
    if (raw === null) return
    const delta = parseInt(raw, 10)
    if (!Number.isFinite(delta) || delta === 0) {
      toast.warn('Enter a non-zero number of days.')
      return
    }
    // Snapshot the requested id set.  Since the endpoint skips already-completed
    // events, negating delta_days on the SAME id set is the cleanest revert —
    // rows that were skipped on apply will also be skipped on revert (still
    // completed), and the un-skipped ones land back at their original time.
    const snapshot = { ids: [...ids], delta_days: delta }
    setBulkBusy(true)
    try {
      const res = await api.put('/api/calendar/bulk/reschedule', { ids, delta_days: delta })
      clearTaskSelection()
      let msg = `Rescheduled ${res.data.rescheduled} of ${res.data.requested} task${res.data.requested === 1 ? '' : 's'} by ${delta} day${Math.abs(delta) === 1 ? '' : 's'}.`
      if (res.data.skipped_completed > 0) msg += ` (${res.data.skipped_completed} completed task(s) skipped.)`
      undo.run({
        ids,
        snapshot: () => snapshot,
        apply: async () => {}, // already fired
        revert: async (snap) => {
          await api.put('/api/calendar/bulk/reschedule', {
            ids: snap.ids, delta_days: -snap.delta_days,
          })
        },
        label: msg,
        onAfter: () => fetchEvents(),
      })
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk reschedule failed')
    } finally { setBulkBusy(false) }
  }
  const [completingEvent, setCompletingEvent] = useState(null)
  const [completionNote, setCompletionNote] = useState('')
  const [addingNote, setAddingNote] = useState(null)
  const [newNote, setNewNote] = useState('')

  useEffect(() => { fetchEvents() }, [])

  // Arriving again with a different ?due= (e.g. clicking the bar's link while
  // already on this page) re-applies the filter.
  useEffect(() => {
    if (dueParam) { setDueDays(dueParam); setFilter('due') }
    else if (tabParam) setFilter(tabParam)
  }, [dueParam, tabParam])

  // ?focus=<id> — scroll the item into view and ring it briefly, once loaded.
  useEffect(() => {
    if (loading || !focusParam) return undefined
    const el = document.getElementById(`task-${focusParam}`)
    if (!el) return undefined
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setHighlightId(focusParam)
    const t = setTimeout(() => setHighlightId(null), 2500)
    return () => clearTimeout(t)
  }, [loading, focusParam])

  usePageShortcut('n', () => navigate('/calendar?new=1'), { enabled: true })
  usePageShortcut('f', () => document.querySelector('input[placeholder*="Search"], input[placeholder*="Filter"], input[type="search"]')?.focus(), { enabled: true })

  const fetchEvents = async () => {
    try {
      const res = await api.get('/api/calendar/tasks')
      setEvents(res.data)
      // Tell the quick-access bar's To-Do badge something changed — but not on
      // the first load, which changed nothing.
      if (loadedOnceRef.current) {
        try { window.dispatchEvent(new Event('lextrack-todos-changed')) } catch { /* best-effort */ }
      }
      loadedOnceRef.current = true
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  // Mirrors Calendar.js handleDelete.  A series member opens the scope picker
  // rather than deleting immediately: events.series_id is ON DELETE CASCADE,
  // so deleting a seed without choosing a scope is a decision the user has to
  // make consciously.  Everything else takes the 5-second-Undo fast path.
  const handleDelete = (event) => {
    if (event.series_id) {
      setSeriesDeletePrompt({ id: event.id, title: event.title, seriesId: event.series_id })
      return
    }
    // Prune the id out of the bulk selection too, or the action bar keeps
    // claiming "1 selected" for a row that is no longer on screen.
    setSelectedTaskIds((prev) => {
      if (!prev.has(event.id)) return prev
      const next = new Set(prev)
      next.delete(event.id)
      return next
    })
    pending.schedule({
      url: `/api/calendar/${event.id}`,
      message: `Task deleted${event.title ? ` — ${event.title}` : ''}`,
      onOptimisticRemove: () => setEvents((prev) => prev.filter((e) => e.id !== event.id)),
      onRestore: () => fetchEvents(),
      onDeleteError: (err) => {
        // usePendingDelete deletes via raw fetch and throws `new Error('DELETE 403')`,
        // so err.response is always undefined here — reading it would surface the
        // raw string to the user.  Map the status to something readable instead.
        const status = Number(String(err?.message || '').replace(/\D/g, '')) || 0
        setError(
          status === 403 ? 'You can only delete events you created.'
          : status === 404 ? 'That event is no longer available to you.'
          : 'Could not delete the task — it has been restored.'
        )
        fetchEvents()
      }
    })
  }

  const commitSeriesDelete = async (scope) => {
    if (!seriesDeletePrompt) return
    const { id, seriesId } = seriesDeletePrompt
    // DELETE ?scope=series removes every row sharing this series_id with no
    // date bound and no undo — most of them are outside the window this page
    // shows, so the count on screen understates what is about to go.  Ask once
    // more, naming that, before anything irreversible happens.
    if (scope === 'series') {
      const visible = events.filter((e) => e.series_id === seriesId || e.id === seriesId).length
      const ok = await ask({
        type: 'confirm',
        title: 'Delete the entire series?',
        message: `This removes every occurrence of "${seriesDeletePrompt.title}", past and future — including occurrences outside the range shown here, so more than the ${visible} visible on this page. This cannot be undone.`,
        confirmLabel: 'Delete entire series',
        danger: true,
      })
      if (!ok) return
    }
    setSeriesDeletePrompt(null)
    try {
      const res = await api.delete(`/api/calendar/${id}?scope=${scope}`)
      const count = res?.data?.deleted_count ?? 1
      // ONE predicate drives both the optimistic removal and the selection
      // prune, so the two can never disagree about what left the page.
      let isGone
      if (scope === 'series') {
        isGone = (e) => e.series_id === seriesId || e.id === seriesId
      } else if (scope === 'future') {
        const anchor = events.find((e) => e.id === id)
        const anchorTime = anchor ? new Date(anchor.start_time).getTime() : Infinity
        isGone = (e) => e.series_id === seriesId && new Date(e.start_time).getTime() >= anchorTime
      } else {
        isGone = (e) => e.id === id
      }
      const gone = new Set(events.filter(isGone).map((e) => e.id))
      setEvents((prev) => prev.filter((e) => !isGone(e)))

      // Prune ONLY what was deleted.  clearTaskSelection() here would drop
      // every tick the user had made, including on rows that still exist.
      setSelectedTaskIds((prev) => {
        if (!prev.size) return prev
        const next = new Set(prev)
        let changed = false
        for (const sid of prev) {
          if (!gone.has(sid)) continue
          next.delete(sid); changed = true
        }
        return changed ? next : prev
      })
      // scope=this on a series SEED promotes a child server-side and repoints
      // every sibling's series_id; refetch so the local rows aren't stale.
      if (scope === 'this' && id === seriesId) fetchEvents()
      toast.success(`Deleted ${count} event${count === 1 ? '' : 's'}.`)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not delete series')
      fetchEvents()
    }
  }

  const handleComplete = async (eventId) => {
    try {
      await api.put(`/api/calendar/${eventId}/complete`, {
        completion_note: completionNote
      })
      setCompletingEvent(null)
      setCompletionNote('')
      fetchEvents()
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred')
    }
  }

  const handleReopen = async (eventId) => {
    try {
      await api.put(`/api/calendar/${eventId}/reopen`)
      fetchEvents()
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred')
    }
  }

  const handleAddNote = async (eventId) => {
    if (!newNote.trim()) return
    try {
      await api.post(`/api/calendar/${eventId}/notes`, { note: newNote })
      setAddingNote(null)
      setNewNote('')
      fetchEvents()
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred')
    }
  }

  const filteredEvents = events
    .filter(e => {
      if (filter === 'active') return !e.is_completed
      if (filter === 'completed') return e.is_completed
      if (filter === 'overdue') return isOverdue(e)
      if (filter === 'today') {
        const days = getPriorityInfo(e).days
        return !e.is_completed && days === 0 && !isOverdue(e)
      }
      if (filter === 'due') return isDueWithin(e, dueDays)
      return true
    })
  // Apply sort — priority uses the existing getPriorityInfo order; date
  // sorts by the instant each item is due (todoDueDate: an Amicus to-do by
  // its due day, not its start); title alphabetical.
  const sortedByPriority = sort.key === 'priority'
    ? [...filteredEvents].sort((a, b) => {
        const pa = getPriorityInfo(a).order
        const pb = getPriorityInfo(b).order
        return sort.dir === 'desc' ? pb - pa : pa - pb
      })
    : sort.apply(filteredEvents, {
        due: (e) => todoDueDate(e),
        title: (e) => (e.title || '').toLowerCase(),
        matter: (e) => (e.matter_title || '').toLowerCase(),
      })

  const overdue = events.filter(e => isOverdue(e))
  const dueToday = events.filter(e => !e.is_completed && getPriorityInfo(e).days === 0 && !isOverdue(e))
  const active = events.filter(e => !e.is_completed)
  const completed = events.filter(e => e.is_completed)
  const dueSoon = events.filter(e => isDueWithin(e, dueDays))

  if (loading) return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <SkeletonText lines={1} className="h-8 mb-2 w-40" />
          <SkeletonText lines={1} className="h-4 w-72" />
        </div>
      </div>
      <div className="space-y-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  )

  return (
    <div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{error}</div>}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">To-Do<HelpButton slug="tasks" /></h2>
          <p className="text-sm text-gray-500 mt-1">Your to-do list — all events, deadlines and tasks in one place</p>
        </div>
        <a href="/calendar"
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          + New Event
        </a>
      </div>

      {/* Priority Legend */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 mb-4">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Priority Key</p>
        <div className="flex flex-wrap gap-4 text-xs">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-gray-900 inline-block"></span>
            <span className="font-medium">Overdue</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-600 inline-block"></span>
            <span>Due Today</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-400 inline-block"></span>
            <span>1-2 Days</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-400 inline-block"></span>
            <span>3-6 Days</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-yellow-400 inline-block"></span>
            <span>7-13 Days</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-400 inline-block"></span>
            <span>14+ Days</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-gray-400 inline-block"></span>
            <span>Completed</span>
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-gray-900 text-white rounded-2xl p-4 cursor-pointer" onClick={() => setFilter('overdue')}>
          <p className="text-xs opacity-75">Overdue</p>
          <p className="text-3xl font-bold">{overdue.length}</p>
        </div>
        <div className="bg-red-600 text-white rounded-2xl p-4 cursor-pointer" onClick={() => setFilter('today')}>
          <p className="text-xs opacity-75">Due Today</p>
          <p className="text-3xl font-bold">{dueToday.length}</p>
        </div>
        <div className="bg-blue-600 text-white rounded-2xl p-4 cursor-pointer" onClick={() => setFilter('active')}>
          <p className="text-xs opacity-75">Total Active</p>
          <p className="text-3xl font-bold">{active.length}</p>
        </div>
        <div className="bg-green-600 text-white rounded-2xl p-4 cursor-pointer" onClick={() => setFilter('completed')}>
          <p className="text-xs opacity-75">Completed</p>
          <p className="text-3xl font-bold">{completed.length}</p>
        </div>
      </div>

      {/* Filter Tabs + Sort dropdown */}
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
        {[
          { id: 'active', label: `Active (${active.length})` },
          { id: 'overdue', label: `Overdue (${overdue.length})` },
          { id: 'today', label: `Due Today (${dueToday.length})` },
          { id: 'due', label: `Due within ${dueDays} days (${dueSoon.length})` },
          { id: 'completed', label: `Completed (${completed.length})` },
          { id: 'all', label: 'All' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === tab.id
                ? 'bg-primary-600 text-white'
                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}>
            {tab.label}
          </button>
        ))}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Sort:</label>
          <select value={`${sort.key}:${sort.dir}`}
            onChange={e => {
              const [key, dir] = e.target.value.split(':')
              // H-T1 stale-closure fix: sort.toggle uses functional setState,
              // so consecutive toggles compose correctly.  But reading sort.dir
              // AFTER a key-change toggle would read the stale pre-toggle value
              // and race the update — one branch per case avoids consulting
              // sort.dir once the first toggle has changed keys.
              if (sort.key !== key) {
                // Switching column: toggle(key) sets {key, 'asc'}.
                sort.toggle(key)
                // If target is 'desc', second toggle flips via functional
                // updater (prev = {key, 'asc'}, same key → flip to 'desc').
                if (dir === 'desc') sort.toggle(key)
              } else if (sort.dir !== dir) {
                // Same key, opposite dir: single flip.
                sort.toggle(key)
              }
            }}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5">
            <option value="priority:asc">Priority (urgent first)</option>
            <option value="due:asc">Due date (soonest first)</option>
            <option value="due:desc">Due date (latest first)</option>
            <option value="title:asc">Title (A→Z)</option>
            <option value="title:desc">Title (Z→A)</option>
            <option value="matter:asc">Matter (A→Z)</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar — sticky above the list when tasks are selected */}
      {selectedTaskIds.size > 0 && (
        <div className="sticky top-2 z-30 bg-primary-50 border border-primary-200 rounded-lg px-4 py-2 mb-3 flex items-center gap-3 shadow-sm">
          <span className="text-sm font-medium text-primary-800">
            {selectedTaskIds.size} selected
          </span>
          <button onClick={handleBulkComplete} disabled={bulkBusy}
            className="text-xs font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 px-3 py-1.5 rounded-lg">
            ✓ Mark complete
          </button>
          <button onClick={handleBulkReschedule} disabled={bulkBusy}
            className="text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 px-3 py-1.5 rounded-lg">
            📅 Reschedule…
          </button>
          <button onClick={clearTaskSelection}
            className="ml-auto text-xs text-gray-500 hover:text-gray-700">Clear selection</button>
        </div>
      )}

      {/* Events List */}
      <div className="space-y-2">
        {sortedByPriority.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
            {events.length === 0 ? (
              <EmptyState
                icon="✅"
                title="No tasks or events yet"
                description="Tasks and calendar events show up here.  Add one from the Calendar to schedule a deadline, hearing, or to-do item."
                action={{ label: 'Go to Calendar', onClick: () => navigate('/calendar') }}
              />
            ) : (
              <EmptyState
                icon="✅"
                variant="filtered"
                title="No tasks match your filters"
                description="Try changing the filter row above to see more items."
                action={{ label: 'Show all', onClick: () => setFilter('all') }}
              />
            )}
          </div>
        ) : (
          sortedByPriority.map(event => {
            const priorityInfo = getPriorityInfo(event)
            return (
              <div key={event.id} id={`task-${event.id}`} className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 ${priorityInfo.rowColor} ${selectedTaskIds.has(event.id) || highlightId === event.id ? 'ring-2 ring-primary-400' : ''}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <input type="checkbox"
                      checked={selectedTaskIds.has(event.id)}
                      onChange={() => toggleTask(event.id)}
                      aria-label={`Select ${event.title || 'task'}`}
                      className="mt-1.5 rounded border-gray-300" />
                    <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${priorityInfo.dot}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm">{getEventIcon(event.event_type)}</span>
                        <p className={`font-semibold text-gray-800 ${event.is_completed ? 'line-through text-gray-400' : ''}`}>
                          {event.title}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityInfo.color}`}>
                          {priorityInfo.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                        <span>
                          {/* Firm wall clock, not the browser's (#821). */}
                          {(() => {
                            const d = toFirmWallClock(event.start_time)
                            return d ? format(d, 'MMM d, yyyy h:mm a') : ''
                          })()}
                        </span>
                        {/* An Amicus to-do runs from the day work starts to the
                            day it is due: say which day that is. */}
                        {isDueAfterStartDay(event) && (() => {
                          const due = firmDueDay(event)
                          return due ? <span className="font-medium text-gray-700">Due {format(due, 'EEE, MMM d, yyyy')}</span> : null
                        })()}
                        {event.matter_title && (
                          <span className="text-primary-600">📁 {event.matter_title}</span>
                        )}
                        {event.location && <span>📍 {event.location}</span>}
                        {event.created_by_name && <span>👤 {event.created_by_name}</span>}
                      </div>
                      {event.description && (
                        <p className="text-xs text-gray-500 mt-1">{event.description}</p>
                      )}
                      {event.notes && (
                        <div className="mt-1 text-xs text-gray-600 bg-gray-50 rounded p-2 whitespace-pre-wrap">
                          📝 {event.notes}
                        </div>
                      )}
                      {event.completion_note && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          ✅ Completion note: "{event.completion_note}"
                        </p>
                      )}
                      {event.reminders?.length > 0 && (
                        <p className="text-xs text-yellow-600 mt-1">
                          🔔 {event.reminders.length} reminder{event.reminders.length > 1 ? 's' : ''} set
                        </p>
                      )}

                      {/* Feedback #829 follow-up — who last changed this and a
                          History expander, same as the Calendar popup.  The
                          creator is already shown above (👤), so no Created
                          line here.  History loads only when opened. */}
                      <EventHistory event={event} compact showCreated={false} />

                      {/* Complete Form */}
                      {completingEvent === event.id && (
                        <div className="mt-2">
                          <textarea
                            value={completionNote}
                            onChange={e => setCompletionNote(e.target.value)}
                            placeholder="Add completion note (optional)..."
                            rows={2}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                          <div className="flex gap-2 mt-1">
                            <button onClick={() => handleComplete(event.id)}
                              className="text-xs bg-green-600 text-white px-3 py-1 rounded">
                              Mark Complete
                            </button>
                            <button onClick={() => setCompletingEvent(null)}
                              className="text-xs text-gray-500 hover:underline">Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Add Note Form */}
                      {addingNote === event.id && (
                        <div className="mt-2">
                          <textarea
                            value={newNote}
                            onChange={e => setNewNote(e.target.value)}
                            placeholder="Add a note..."
                            rows={2}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                          <div className="flex gap-2 mt-1">
                            <button onClick={() => handleAddNote(event.id)}
                              className="text-xs bg-blue-600 text-white px-3 py-1 rounded">
                              Save Note
                            </button>
                            <button onClick={() => setAddingNote(null)}
                              className="text-xs text-gray-500 hover:underline">Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-xs text-gray-500">
                      {eventTypeLabel(event.event_type)}
                    </span>
                    {/* Only the creator or an admin can complete / reopen / annotate / delete.
                        Backend enforces this — hide the buttons to avoid 403 on click. */}
                    {(user?.id === event.created_by || user?.role === 'admin' || user?.permissions?.is_admin) && (
                      <>
                        {!event.is_completed ? (
                          <button onClick={() => { setCompletingEvent(event.id); setCompletionNote('') }}
                            className="text-xs text-green-600 hover:underline font-medium">
                            ✓ Complete
                          </button>
                        ) : (
                          <button onClick={() => handleReopen(event.id)}
                            className="text-xs text-gray-500 hover:underline">
                            ↩ Reopen
                          </button>
                        )}
                        <button onClick={() => { setAddingNote(event.id); setNewNote('') }}
                          className="text-xs text-blue-500 hover:underline">
                          + Note
                        </button>
                        <button onClick={() => handleDelete(event)}
                          className="text-xs text-red-500 hover:underline">
                          🗑 Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {seriesDeletePrompt && (
        <SeriesDeleteModal
          prompt={seriesDeletePrompt}
          onClose={() => setSeriesDeletePrompt(null)}
          onConfirm={commitSeriesDelete}
        />
      )}
    </div>
  )
}
